import React, { useEffect, useMemo, useRef, useState } from "react";
import { Globe, Languages, Check, Search } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

// Reusable language picker (custom dropdown — no portal)
export function LanguagePicker({ trigger, align = "right", showGroups = true }) {
  const { languages, lang, setLang, translating } = useLanguage();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = languages.filter(
      (l) => !term || l.name.toLowerCase().includes(term) || l.native_name.toLowerCase().includes(term) || l.code.includes(term)
    );
    if (!showGroups) return { All: list };
    return {
      International: list.filter((l) => l.group === "International"),
      "Indian Languages": list.filter((l) => l.group === "Indian"),
    };
  }, [languages, q, showGroups]);

  const choose = (l) => {
    setLang({ code: l.code, name: l.name, direction: l.direction });
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrap} className="relative">
      <span onClick={() => setOpen((o) => !o)}>{trigger}</span>
      {open && (
        <div className={`absolute top-full z-[60] mt-2 w-80 overflow-hidden rounded-lg border border-border bg-white shadow-2xl ${align === "right" ? "right-0" : "left-0"}`} data-testid="language-picker">
          <div className="flex items-center gap-2 border-b px-3 py-2 bg-navy text-white">
            <Search className="h-4 w-4 opacity-70" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 48 languages…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-white/50"
              data-testid="language-search-input"
            />
          </div>
          {translating && <div className="px-3 py-1.5 text-[11px] text-oldglory-red bg-red-50">Translating page…</div>}
          <div className="max-h-72 overflow-y-auto py-1">
            {Object.entries(filtered).map(([group, items]) =>
              items.length ? (
                <div key={group}>
                  <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{group}</div>
                  {items.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => choose(l)}
                      className="flex w-full items-center justify-between px-3 py-2 text-sm hover:bg-parchment transition-colors"
                      data-testid={`lang-option-${l.code}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-medium text-ink">{l.name}</span>
                        <span className="text-muted-foreground" dir={l.direction}>{l.native_name}</span>
                      </span>
                      {lang.code === l.code && <Check className="h-4 w-4 text-oldglory-red" />}
                    </button>
                  ))}
                </div>
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function TranslateMenu() {
  return (
    <LanguagePicker
      showGroups={false}
      trigger={
        <button className="flex items-center gap-1.5 text-white/90 transition-colors" data-testid="translate-btn">
          <Languages className="h-4 w-4" />
          <span className="text-[13px]">Translate</span>
          <span className="text-[11px] opacity-60">▾</span>
        </button>
      }
    />
  );
}

export function LanguageMenu() {
  const { lang } = useLanguage();
  return (
    <LanguagePicker
      trigger={
        <button className="flex items-center gap-1.5 text-white/90 transition-colors" data-testid="select-language-btn">
          <Globe className="h-4 w-4" />
          <span className="text-[13px]" dir={lang.direction}>{lang.code === "en" ? "Select Language" : lang.name}</span>
          <span className="text-[11px] opacity-60">▾</span>
        </button>
      }
    />
  );
}
